import { useState, useEffect, useCallback } from "react";
import { Plus, GripVertical, Pencil, Trash2, X, Check } from "lucide-react";
import axiosInstance from "../../../API/interceptor";
import Swal from "sweetalert2";
import { CONFIRM_CLASSES, successToast, errorToast } from "../swalTheme";

const SPECIAL_BUTTONS = ["START", "SEND BACK", "FORWARD TO"];

const TAG_CLASSES = {
  INITIAL: "bg-blue-100 text-blue-700",
  MIDDLE:  "bg-yellow-100 text-yellow-700",
  FINAL:   "bg-green-100 text-green-700",
};

const HEADER_GRADIENT = "bg-[#10B981] shadow-[0_4px_14px_rgba(16,185,129,.35)]";
const PILL_CLASSES = "bg-[#6c5ffc] text-white";

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 transition-all";

export default function TaskStatusTab() {
  const [workspaces,   setWorkspaces]   = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [dragIdx,      setDragIdx]      = useState(null);
  const [modal,        setModal]        = useState(null);

  useEffect(() => {
    axiosInstance.get("/task_assignment/get_product_codes")
      .then(res => {
        const codes = res.data.product_codes || [];
        setWorkspaces(codes);
        if (codes.length > 0)
          setSelectedCode(parseInt(Object.keys(codes[0])[0]));
      }).catch(console.error);
  }, []);

  const loadStatus = useCallback((code) => {
    setLoading(true);
    axiosInstance.get(`/task_assignment/status_flow/${code}`)
      .then(res => {
        const raw = res.data.status || {};
        setRows(Object.entries(raw).map(([key, val]) => ({
          index:        parseInt(key),
          status:       val.status       || "",
          button:       val.button       || "",
          tag:          val.tag          || "MIDDLE",
          show_buttons: val.show_buttons || [],
        })));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedCode !== null) loadStatus(selectedCode);
  }, [selectedCode, loadStatus]);

  // ── Drag reorder ─────────────────────────────────────────────
  function onDragStart(idx) { setDragIdx(idx); }
  function onDragOver(e, idx) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const updated = [...rows];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(idx, 0, moved);
    setDragIdx(idx);
    setRows(updated);
  }
  function onDragEnd() {
    setDragIdx(null);
    const newOrder = {};
    rows.forEach((r, i) => {
      newOrder[i + 1] = { status: r.status, button: r.button, tag: r.tag, show_buttons: r.show_buttons };
    });
    axiosInstance.post(`/task_assignment/change_status/${selectedCode}`, { status: newOrder })
      .then(() => loadStatus(selectedCode))
      .catch(console.error);
  }

  // ── Toggle show button — click on pill to remove, click on + to add ─
  function removeShowButton(rowIndex, btn) {
    const row     = rows.find(r => r.index === rowIndex);
    const updated = row.show_buttons.filter(b => b !== btn);
    saveShowButtons(rowIndex, updated);
  }

  function addShowButton(rowIndex, btn) {
    const row     = rows.find(r => r.index === rowIndex);
    if (row.show_buttons.includes(btn)) return;
    saveShowButtons(rowIndex, [...row.show_buttons, btn]);
  }

  function saveShowButtons(rowIndex, updated) {
    // Optimistic update
    setRows(prev => prev.map(r => r.index === rowIndex ? { ...r, show_buttons: updated } : r));
    axiosInstance.post(`/task_assignment/update_show_buttons/${selectedCode}`, { index: rowIndex, show_buttons: updated })
      .catch(() => loadStatus(selectedCode)); // rollback on error
  }

  // ── Delete ───────────────────────────────────────────────────
  function deleteStatus(index) {
    Swal.fire({
      title: "Delete status?",
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: CONFIRM_CLASSES,
    }).then(r => {
        if (!r.isConfirmed) return;
        axiosInstance.post(`/task_assignment/delete_status/${selectedCode}`, { index })
          .then(() => { successToast.fire({ icon: "success", title: "Deleted!" }); loadStatus(selectedCode); })
          .catch(() => errorToast.fire({ icon: "error", title: "Could not delete" }));
      });
  }

  // ── Add / Edit ───────────────────────────────────────────────
  function submitModal(form) {
    const { type, index, status, button, tag } = form;
    if (!status || !button) { errorToast.fire({ icon: "error", title: "All fields required" }); return; }
    const req = type === "add"
      ? axiosInstance.post(`/task_assignment/add_status/${selectedCode}`, { status, button, tag })
      : axiosInstance.post(`/task_assignment/edit_status/${selectedCode}`, { index, updatedStatus: { status, button, tag } });
    req.then(res => {
      setModal(null);
      successToast.fire({ icon: "success", title: res.data.message });
      loadStatus(selectedCode);
    }).catch(err => errorToast.fire({ icon: "error", title: err.response?.data?.message || "Failed" }));
  }

  const allButtons = rows.map(r => r.button);

  return (
    <div>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4">
        <select
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 min-w-[200px]"
          value={selectedCode || ""}
          onChange={e => setSelectedCode(parseInt(e.target.value))}
        >
          {workspaces.map(code => {
            const [key, val] = Object.entries(code)[0];
            return <option key={key} value={key}>{val}</option>;
          })}
        </select>

        <button
          onClick={() => setModal({ type: "add", status: "", button: "", tag: "INITIAL" })}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg cursor-pointer border-none hover:opacity-90 transition-all ${HEADER_GRADIENT}`}
        >
          <Plus size={14} /> Add Status
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-gray-100">
              <th className="px-3 py-3 w-8"></th>
              <th className="px-4 py-3 text-left font-semibold">Index</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Button</th>
              <th className="px-4 py-3 text-left font-semibold">Tag</th>
              <th className="px-4 py-3 text-left font-semibold">Show Buttons</th>
              <th className="px-4 py-3 text-left font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(7)].map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-gray-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No statuses found</td></tr>
            ) : rows.map((row, idx) => {
              const tagClass = TAG_CLASSES[row.tag] || TAG_CLASSES.MIDDLE;
              // Buttons not already shown
              const availableToAdd = allButtons.filter(b => b !== row.button && !row.show_buttons.includes(b));

              return (
                <tr
                  key={row.index}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={e => onDragOver(e, idx)}
                  onDragEnd={onDragEnd}
                  className={`transition-colors cursor-grab ${dragIdx === idx ? "bg-indigo-50" : "bg-white hover:bg-slate-50"}`}
                >
                  <td className="px-3 py-3 text-slate-300"><GripVertical size={14} /></td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">{row.index}</td>
                  <td className="px-4 py-3 font-medium text-slate-700">{row.status}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{row.button}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tagClass}`}>
                      {row.tag}
                    </span>
                  </td>

                  {/* ── Show Buttons — x BUTTON pills like HTML ── */}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {/* Existing show_buttons as removable pills */}
                      {row.show_buttons.map(btn => (
                        <span
                          key={btn}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold cursor-default ${PILL_CLASSES}`}
                        >
                          <span
                            className="cursor-pointer hover:opacity-70 font-bold"
                            onClick={() => removeShowButton(row.index, btn)}
                            title={`Remove ${btn}`}
                          >×</span>
                          {btn}
                        </span>
                      ))}

                      {/* Dropdown to add more buttons */}
                      {availableToAdd.length > 0 && (
                        <AddButtonDropdown
                          options={availableToAdd}
                          onSelect={btn => addShowButton(row.index, btn)}
                        />
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setModal({ type: "edit", index: row.index, status: row.status, button: row.button, tag: row.tag })}
                        className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 transition-colors"
                        title="Edit"
                      >
                        <Pencil size={13} />
                      </button>
                      {SPECIAL_BUTTONS.includes(row.button) ? (
                        <button disabled className="p-1.5 rounded-lg text-gray-300 cursor-not-allowed">
                          <Trash2 size={13} />
                        </button>
                      ) : (
                        <button
                          onClick={() => deleteStatus(row.index)}
                          className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <StatusModal modal={modal} onChange={setModal} onSubmit={submitModal} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

// ── Small + dropdown to add a show button ────────────────────────
function AddButtonDropdown({ options, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-center w-6 h-6 rounded border border-dashed border-gray-300 text-gray-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors"
        title="Add button"
      >
        <Plus size={11} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.1)] min-w-max py-1">
            {options.map(btn => (
              <div
                key={btn}
                className="px-3 py-1.5 text-xs font-semibold cursor-pointer hover:bg-indigo-50 hover:text-indigo-700 text-slate-700"
                onClick={() => { onSelect(btn); setOpen(false); }}
              >
                {btn}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Add / Edit Modal ─────────────────────────────────────────────
function StatusModal({ modal, onChange, onSubmit, onClose }) {
  const isEdit = modal.type === "edit";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35">
      <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] w-full max-w-md mx-4">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h5 className="font-semibold text-slate-700">{isEdit ? "Edit Status" : "Add New Status"}</h5>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Status</label>
            <input className={inputCls} value={modal.status}
              onChange={e => onChange(m => ({ ...m, status: e.target.value }))} placeholder="e.g. In Review" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Button</label>
            <input className={inputCls} value={modal.button}
              onChange={e => onChange(m => ({ ...m, button: e.target.value }))} placeholder="e.g. REVIEW" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Tag</label>
            <select className={inputCls} value={modal.tag} onChange={e => onChange(m => ({ ...m, tag: e.target.value }))}>
              <option value="INITIAL">INITIAL</option>
              <option value="MIDDLE">MIDDLE</option>
              <option value="FINAL">FINAL</option>
            </select>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={() => onSubmit(modal)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg border-none cursor-pointer hover:opacity-90 ${HEADER_GRADIENT}`}
          >
            <Check size={13} /> {isEdit ? "Save Changes" : "Add Status"}
          </button>
        </div>
      </div>
    </div>
  );
}